import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  getAdminStats,
  getAdminOrderStats,
  getAdminOrders,
  getAdminMenuItems,
  getAdminMenuCategories,
} from '../lib/api';
import { todayMexicoYmd } from '../lib/dateMexico';
import {
  Calendar,
  Package,
  MessageSquare,
  Star,
  Clock,
  CheckCircle,
  ClipboardList,
  Settings,
  Truck,
  ShoppingBag,
  AlertTriangle,
} from 'lucide-react';

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        if (PIZAS_MODE) {
          const [orderStats, pendingPaymentOrders, menuItems, menuCategories] = await Promise.all([
            getAdminOrderStats(todayMexicoYmd()),
            getAdminOrders('pending_payment', todayMexicoYmd()),
            getAdminMenuItems(),
            getAdminMenuCategories(),
          ]);
          setStats({
            orders_today: Number(orderStats?.orders_today || 0),
            pending_review: Number(orderStats?.pending || 0),
            in_kitchen: Number(orderStats?.in_kitchen || 0),
            pending_payment: Array.isArray(pendingPaymentOrders) ? pendingPaymentOrders.length : 0,
            menu_items: Array.isArray(menuItems) ? menuItems.length : 0,
            menu_categories: Array.isArray(menuCategories) ? menuCategories.length : 0,
            online_sales_total: Number(orderStats?.online_sales_total || 0),
            online_sales_count: Number(orderStats?.online_sales_count || 0),
            low_stock_count: Number(orderStats?.low_stock_count || 0),
            low_stock_items: Array.isArray(orderStats?.low_stock_items)
              ? orderStats.low_stock_items
              : [],
          });
        } else {
          const data = await getAdminStats();
          setStats(data);
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando estadísticas...</div>
      </div>
    );
  }

  const statCards = PIZAS_MODE
    ? [
        {
          title: 'Pedidos de Hoy',
          value: stats?.orders_today || 0,
          icon: ClipboardList,
          color: 'bg-primary/10 text-primary',
        },
        {
          title: 'Por Revisar',
          value: stats?.pending_review || 0,
          icon: Clock,
          color: 'bg-warning/10 text-warning',
        },
        {
          title: 'En Cocina',
          value: stats?.in_kitchen || 0,
          icon: CheckCircle,
          color: 'bg-success/10 text-success',
        },
        {
          title: 'Pago Pendiente',
          value: stats?.pending_payment || 0,
          icon: Calendar,
          color: 'bg-accent/10 text-accent',
        },
        {
          title: 'Ventas en línea (hoy)',
          value: `$${Number(stats?.online_sales_total || 0).toLocaleString('es-MX')}`,
          subtitle: `${stats?.online_sales_count || 0} pedido(s) Web / WhatsApp`,
          icon: ShoppingBag,
          color: 'bg-primary/10 text-primary',
        },
        {
          title: 'Pizzas en Menú',
          value: stats?.menu_items || 0,
          icon: Package,
          color: 'bg-sage-300/20 text-sage-500',
        },
        {
          title: 'Categorías',
          value: stats?.menu_categories || 0,
          icon: Truck,
          color: 'bg-primary/10 text-primary',
        },
      ]
    : [
        {
          title: 'Citas de Hoy',
          value: stats?.appointments?.today || 0,
          icon: Clock,
          color: 'bg-primary/10 text-primary',
        },
        {
          title: 'Citas Pendientes',
          value: stats?.appointments?.pending || 0,
          icon: Calendar,
          color: 'bg-warning/10 text-warning',
        },
        {
          title: 'Total Citas',
          value: stats?.appointments?.total || 0,
          icon: CheckCircle,
          color: 'bg-success/10 text-success',
        },
        {
          title: 'Productos Activos',
          value: stats?.products?.active || 0,
          icon: Package,
          color: 'bg-sage-300/20 text-sage-500',
        },
        {
          title: 'Reseñas Pendientes',
          value: stats?.reviews?.pending || 0,
          icon: MessageSquare,
          color: 'bg-accent/10 text-accent',
        },
        {
          title: 'Calificación Promedio',
          value: stats?.reviews?.avg_rating || '0.0',
          icon: Star,
          color: 'bg-warning/10 text-warning',
        },
      ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">
          {PIZAS_MODE ? 'Resumen operativo de pedidos, cocina y menú.' : 'Bienvenido al panel de administración'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <Card key={index} className="border border-border" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                  <p className="text-3xl font-bold text-foreground">{card.value}</p>
                  {card.subtitle ? (
                    <p className="text-xs text-muted-foreground mt-1">{card.subtitle}</p>
                  ) : null}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${card.color}`}>
                  <card.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-heading">Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {PIZAS_MODE ? (
              <>
                <Link to="/admin/pedidos" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Bandeja de Pedidos</p>
                      <p className="text-sm text-muted-foreground">Aceptar, rechazar y mover estado de cocina</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/menu" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Gestionar Menú</p>
                      <p className="text-sm text-muted-foreground">Pizzas, categorías, ingredientes e imágenes</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/config-tienda" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Configurar Tienda</p>
                      <p className="text-sm text-muted-foreground">Radio de entrega, tiempos y mensajes</p>
                    </div>
                  </div>
                </Link>
              </>
            ) : (
              <>
                <Link to="/admin/productos" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Gestionar Productos</p>
                      <p className="text-sm text-muted-foreground">Agregar, editar o eliminar servicios</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/citas" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Ver Citas</p>
                      <p className="text-sm text-muted-foreground">Confirmar o cancelar citas</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/resenas" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Moderar Reseñas</p>
                      <p className="text-sm text-muted-foreground">Aprobar o rechazar comentarios</p>
                    </div>
                  </div>
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-heading">{PIZAS_MODE ? 'Almacén' : 'Información del Sistema'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {PIZAS_MODE ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">Insumos con stock bajo</p>
                    <Link
                      to="/admin/almacen?low=1"
                      className="inline-block mt-2 text-4xl font-bold text-amber-900 hover:text-amber-950 tabular-nums"
                      title="Ver almacén"
                    >
                      {stats?.low_stock_count ?? 0}
                    </Link>
                    {stats?.low_stock_items?.length > 0 ? (
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                        {stats.low_stock_items.map((i) => i.name).join(' · ')}
                        {(stats?.low_stock_count || 0) > stats.low_stock_items.length
                          ? ` · +${stats.low_stock_count - stats.low_stock_items.length} más`
                          : ''}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-2">Todo el almacén está por encima del mínimo.</p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
            {/* En modo pizzería los totales ya aparecen en las tarjetas superiores; no se repiten aquí. */}
            {!PIZAS_MODE ? (
            <>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">{PIZAS_MODE ? 'Pedidos hoy' : 'Total Productos'}</span>
              <span className="font-medium">{PIZAS_MODE ? stats?.orders_today || 0 : stats?.products?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">{PIZAS_MODE ? 'Por revisar' : 'Total Reseñas'}</span>
              <span className="font-medium">{PIZAS_MODE ? stats?.pending_review || 0 : stats?.reviews?.total || 0}</span>
            </div>
            {PIZAS_MODE ? (
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Ventas en línea (hoy)</span>
                <span className="font-medium text-right">
                  ${Number(stats?.online_sales_total || 0).toLocaleString('es-MX')}
                  <span className="block text-xs text-muted-foreground font-normal">
                    {stats?.online_sales_count || 0} pedidos
                  </span>
                </span>
              </div>
            ) : null}
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">{PIZAS_MODE ? 'Pizzas en menú' : 'Total Citas'}</span>
              <span className="font-medium">{PIZAS_MODE ? stats?.menu_items || 0 : stats?.appointments?.total || 0}</span>
            </div>
            </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
